import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Heart, Users, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

export default function RoleSelection() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setAuth = useAuthStore((s) => s.setAuth);
  const [selectedRole, setSelectedRole] = useState('family_caregiver');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.put('/auth/role', {
        role: selectedRole
      });

      setAuth({ ...user, role: selectedRole });
      toast.success('Role updated successfully!');

      if (selectedRole === 'family_caregiver') {
        navigate('/care-profile/setup');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg border-0 shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-teal-500 flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900 font-['Manrope']">ElderLink</span>
          </div>
          <CardTitle className="text-2xl font-bold font-['Manrope']">Welcome!</CardTitle>
          <CardDescription>Tell us how you'll be using ElderLink</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4">
            <button
              type="button"
              onClick={() => setSelectedRole('family_caregiver')}
              className={`flex items-start gap-4 p-4 sm:p-6 rounded-xl border-2 text-left transition-all ${
                selectedRole === 'family_caregiver' 
                  ? 'border-teal-500 bg-teal-50' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid="family-role-btn"
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                selectedRole === 'family_caregiver' ? 'bg-teal-500' : 'bg-slate-100'
              }`}>
                <Users className={`w-6 h-6 ${selectedRole === 'family_caregiver' ? 'text-white' : 'text-slate-400'}`} />
              </div>
              <div>
                <h3 className={`font-semibold text-lg ${selectedRole === 'family_caregiver' ? 'text-teal-700' : 'text-slate-700'}`}>
                  Family Caregiver
                </h3>
                <p className="text-slate-500 text-sm mt-1">
                  I'm caring for an elderly loved one and want to coordinate care with my family.
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setSelectedRole('independent_caregiver')}
              className={`flex items-start gap-4 p-4 sm:p-6 rounded-xl border-2 text-left transition-all ${
                selectedRole === 'independent_caregiver' 
                  ? 'border-teal-500 bg-teal-50' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid="caregiver-role-btn"
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                selectedRole === 'independent_caregiver' ? 'bg-teal-500' : 'bg-slate-100'
              }`}>
                <Briefcase className={`w-6 h-6 ${selectedRole === 'independent_caregiver' ? 'text-white' : 'text-slate-400'}`} />
              </div>
              <div>
                <h3 className={`font-semibold text-lg ${selectedRole === 'independent_caregiver' ? 'text-teal-700' : 'text-slate-700'}`}>
                  Independent Caregiver
                </h3>
                <p className="text-slate-500 text-sm mt-1">
                  I provide professional caregiving services and want to connect with families.
                </p>
              </div>
            </button>
          </div>

          <Button
            onClick={handleSubmit}
            className="w-full h-12 bg-slate-900 hover:bg-slate-800 rounded-lg text-white"
            disabled={loading}
            data-testid="continue-btn"
          >
            {loading ? 'Saving...' : 'Continue'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
